import { Button, Chip, CircularProgress, Stack, Tooltip } from '@mui/material';

import {
  useApplySubgroup,
  useSubgroupDrift,
  type Subgroup
} from '../../api/useSubgroups';

/**
 * Per-subgroup drift indicator: "N rules differ from subgroup" + a one-click
 * "Apply to drifted". MANUAL ONLY — this badge is the operator's signal that a
 * re-apply is warranted (editing the snapshot never auto-prompts). The query
 * is gated so it only runs for the currently-selected subgroup. Reuses the
 * same `entry:apply-subgroup` mutation as the dialog (no bespoke error toast).
 */

interface Props {
  subgroup: Subgroup;
  /** Only fetch drift for the active subgroup — the query is otherwise idle. */
  enabled: boolean;
}

export function SubgroupDriftBadge({ subgroup, enabled }: Props) {
  const { data, isLoading } = useSubgroupDrift(subgroup._id, enabled);
  const applyMutation = useApplySubgroup();

  if (!enabled) return null;
  if (isLoading && !data) {
    return <CircularProgress size={12} sx={{ ml: 0.5 }} />;
  }

  const driftCount = data?.drifted.length ?? 0;
  if (driftCount === 0) {
    return (
      <Chip
        label="in sync"
        size="small"
        variant="outlined"
        color="success"
        sx={{ fontSize: '0.62rem', height: 18 }}
      />
    );
  }

  return (
    <Stack direction="row" alignItems="center" spacing={0.5}>
      <Tooltip
        title={`${driftCount} member rule(s) differ from this subgroup's config snapshot.`}
      >
        <Chip
          label={`${driftCount} differ`}
          size="small"
          variant="outlined"
          color="warning"
          sx={{ fontSize: '0.62rem', height: 18 }}
        />
      </Tooltip>
      <Button
        size="small"
        variant="outlined"
        color="warning"
        disabled={applyMutation.isLoading}
        onClick={() =>
          void applyMutation
            .mutateAsync({ id: subgroup._id, groupId: subgroup.groupId })
            .catch(() => {})
        }
        sx={{ fontSize: '0.6rem', py: 0, px: 0.8, minWidth: 0 }}
      >
        {applyMutation.isLoading ? (
          <CircularProgress size={11} color="inherit" />
        ) : (
          'Apply to drifted'
        )}
      </Button>
    </Stack>
  );
}
